import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export default function Register() {
	const { register } = useAuth();
	const navigate = useNavigate();
	const [form, setForm] = useState({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		confirmPassword: "",
		pin: "",
		confirmPin: "",
	});
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	
	const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
	
	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		
		if (form.password !== form.confirmPassword) {
			return setError("Passwords do not match.");
		}
		if (form.pin !== form.confirmPin) {
			return setError("PINs do not match.");
		}
		if (form.password.length < 8) {
			return setError("Password must be at least 8 characters.");
		}
		if (!/^\d{4,8}$/.test(form.pin)) {
			return setError("PIN must be 4–8 digits (numbers only).");
		}
		
		setLoading(true);
		try {
			await register({
				firstName: form.firstName,
				lastName: form.lastName,
				email: form.email,
				password: form.password,
				pin: form.pin,
			});
			// Always redirect to TOTP setup after registration
			navigate("/totp-setup");
		} catch (err) {
			setError(err.response?.data?.error || "Registration failed.");
		} finally {
			setLoading(false);
		}
	};
	
	return (
		<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
		border border-farm-tan/30 dark:border-gray-700
		transition-colors duration-200">
		<h2 className="text-2xl font-bold text-farm-green dark:text-green-400
		mb-2 text-center">
		Create Your Account
		</h2>
		<p className="text-center text-sm text-gray-500 dark:text-gray-400
		mb-6">
		Your PIN is used for quick ordering via QR code.
		</p>
		
		{error && (
			<div className="bg-red-50 dark:bg-red-900/30 border border-red-300
			dark:border-red-600 text-red-700 dark:text-red-300
			rounded-lg p-3 mb-4 text-sm">
			{error}
			</div>
		)}
		
		<form onSubmit={handleSubmit} className="space-y-4">
		<div className="grid grid-cols-2 gap-4">
		<Field
		label="First Name"
		type="text"
		value={form.firstName}
		onChange={set("firstName")}
		placeholder="Jane"
		/>
		<Field
		label="Last Name"
		type="text"
		value={form.lastName}
		onChange={set("lastName")}
		placeholder="Doe"
		/>
		</div>
		
		<Field
		label="Email Address"
		type="email"
		value={form.email}
		onChange={set("email")}
		placeholder="you@example.com"
		/>
		<Field
		label="Password (min 8 chars)"
		type="password"
		value={form.password}
		onChange={set("password")}
		placeholder="••••••••"
		/>
		<Field
		label="Confirm Password"
		type="password"
		value={form.confirmPassword}
		onChange={set("confirmPassword")}
		placeholder="••••••••"
		/>
		
		<div className="grid grid-cols-2 gap-4">
		<Field
		label="PIN (4–8 digits)"
		type="password"
		value={form.pin}
		onChange={set("pin")}
		placeholder="1234"
		maxLength={8}
		inputMode="numeric"
		/>
		<Field
		label="Confirm PIN"
		type="password"
		value={form.confirmPin}
		onChange={set("confirmPin")}
		placeholder="1234"
		maxLength={8}
		inputMode="numeric"
		/>
		</div>
		
		<div className="bg-yellow-50 dark:bg-yellow-900/30 border
		border-yellow-300 dark:border-yellow-700 rounded-lg
		p-3 text-sm text-yellow-800 dark:text-yellow-300">
		💡 Remember your PIN — you'll need it when scanning your QR code
		to place quick orders. After registering you will be asked to set
		up two-factor authentication.
		</div>
		
		<button
		type="submit"
		disabled={loading}
		className="w-full bg-farm-green text-white py-3 rounded-xl
		font-semibold hover:bg-green-700 transition-colors
		disabled:opacity-60"
		>
		{loading ? "Creating Account…" : "Create Account & Set Up 2FA →"}
		</button>
		</form>
		
		<p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
		Already have an account?{" "}
		<Link
		to="/login"
		className="text-farm-green dark:text-green-400 font-semibold
		hover:underline"
		>
		Sign in
		</Link>
		</p>
		</div>
	);
}

function Field({
	label,
	type,
	value,
	onChange,
	placeholder,
	maxLength,
	inputMode,
}) {
	return (
		<div>
		<label className="block text-sm font-medium text-gray-700
		dark:text-gray-300 mb-1">
		{label}
		</label>
		<input
		type={type}
		required
		value={value}
		onChange={onChange}
		placeholder={placeholder}
		maxLength={maxLength}
		inputMode={inputMode}
		className="w-full border border-gray-300 dark:border-gray-600
		rounded-lg px-3 py-2 bg-white dark:bg-gray-700
		text-gray-800 dark:text-gray-100
		placeholder-gray-400 dark:placeholder-gray-500
		focus:outline-none focus:ring-2 focus:ring-farm-green
		transition-colors"
		/>
		</div>
	);
}
